import {compactCaptureOrigins,resolveCaptureOrigins} from '../template/captureDecisionOrigins';
import { readDesignError } from '../specs/designErrors';
import { withStudioMutationFeedback } from './studioMutationFeedback';
import {recordDecisionChanges} from '../template/decisionJournal';
/**
 * Module Studio (UPT-2079 lot L0) — fournisseur de données : lit
 * `/capabilities` une fois par session, compose le dépôt RESSOURCE PAR
 * RESSOURCE (REST quand la capacité est servie, démonstration sinon) et expose
 * `useModuleStudio()`.
 *
 * Le dépôt exposé est un COMPOSITE : chaque méthode est routée selon
 * `METHOD_RESOURCE`. Le fournisseur n'appelle jamais le dépôt REST d'une
 * ressource non servie. Monté dans `ModuleStudioChrome`.
 */
import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import {
  loadModuleStudioCapabilities,
  ALL_DEMO_CAPABILITIES,
  ModuleStudioUnreachableError,
} from './capabilities';
import type { ModuleStudioCapabilities } from './contracts';
import {
  METHOD_RESOURCE,
  type ModuleStudioRepository,
  type ModuleStudioResource,
} from './ModuleStudioRepository';
import { getSharedFixtureRepository, type FixtureRepository } from './fixtureRepository';
import { cacheRepositoryReads } from './repositoryReadCache';
import { RestRepository } from './restRepository';
import { DemoProfileBanner, ServerUnreachable } from '../shared/ServerUnreachable';

export type ModuleStudioContextValue = {
  repository: ModuleStudioRepository;
  /** Ressources encore lues depuis les jeux de démonstration. */
  demoResources: ModuleStudioResource[];
  capabilities: ModuleStudioCapabilities;
  /** Vide le stockage local et recharge les jeux d'origine. */
  resetDemo: () => void;
  ready: boolean;
  /**
   * Relecture du 18.10 (W2) : le serveur n'a pas servi les capacités et le profil Vite ne demande pas la
   * démonstration. Aucune lecture ni mutation ne passe (le dépôt lève) ; `retry()` relance la lecture.
   */
  unreachable: ModuleStudioUnreachableError | null;
  retry: () => void;
  /** Vrai quand TOUT est en démonstration parce que le profil Vite l'a demandé (bandeau visible). */
  demoProfile: boolean;
};

/** Dépôt « serveur injoignable » : chaque méthode lève, rien n'est lu ni écrit (ni serveur, ni démonstration). */
const unreachableRepository = (error: ModuleStudioUnreachableError): ModuleStudioRepository => {
  const repository = {} as Record<string, () => Promise<never>>;
  (Object.keys(METHOD_RESOURCE) as (keyof ModuleStudioRepository)[]).forEach((method) => {
    repository[method] = () => Promise.reject(error);
  });
  return repository as unknown as ModuleStudioRepository;
};

const ModuleStudioContext = createContext<ModuleStudioContextValue | null>(null);

const currentUserName = (): string => {
  const context = getAuthContextState();
  return context?.email ?? '';
};

/** Composite : chaque méthode est routée selon METHOD_RESOURCE (exporté pour les tests). */
export const composeRepository = (
  capabilities: ModuleStudioCapabilities,
  fixture: FixtureRepository,
  rest: RestRepository
): ModuleStudioRepository => {
  const composite = {} as ModuleStudioRepository;
  (Object.keys(METHOD_RESOURCE) as (keyof ModuleStudioRepository)[]).forEach((method) => {
    const resource = METHOD_RESOURCE[method];
    const served = capabilities.resources[resource] === true;
    const implementation = (served ? rest : fixture) as unknown as Record<
      string,
      (...args: unknown[]) => unknown
    >;
    (composite as unknown as Record<string, unknown>)[method] = async (...args: unknown[]) => {
      try { return await implementation[method](...args); }
      catch (cause) { readDesignError(cause); throw cause; }
    };
  });
  const getDraft = composite.getModuleSpecDraft.bind(composite);
  composite.getModuleSpecDraft = async id => {const value=await getDraft(id);return value?{...value,spec:await resolveCaptureOrigins(value.spec,composite)}:null;};
  const getVersion = composite.getModuleSpecVersion.bind(composite);
  composite.getModuleSpecVersion = async (id,version) => {const value=await getVersion(id,version);return {...value,spec:await resolveCaptureOrigins(value.spec,composite)};};
  const putDraft = composite.putModuleSpecDraft.bind(composite);
  composite.putModuleSpecDraft = async (id, input) => {
    const previous = await composite.getModuleSpecDraft(id);
    const spec = previous ? await recordDecisionChanges(previous.spec, input.spec) : input.spec;
    return putDraft(id, {...input, spec:await compactCaptureOrigins(spec,composite)});
  };
  return withStudioMutationFeedback(cacheRepositoryReads(composite));
};

/*
 * Revue CORE 11.10 (intégration) : les pages appellent useModuleStudio() AVANT de rendre le chrome qui monte le
 * fournisseur ; le contexte était donc vide (« doit être utilisé sous <ModuleStudioDataProvider> »). L'état vit
 * désormais au niveau du module (capacités chargées une fois par session, valeur mémorisée par capacités) : le hook
 * fonctionne avec ou sans fournisseur, et le fournisseur ne fait que transmettre la même valeur.
 */
/*
 * Relecture du 18.10 (W2) : l'état partagé distingue « en cours », « servi » et « injoignable ». Un échec n'est
 * JAMAIS mis en cache : `loadStarted` retombe à faux, la prochaine lecture (nouvel abonnement, `retry()`) relance
 * la requête. La démonstration n'arrive que par `loadModuleStudioCapabilities` (profil Vite explicite).
 */
type SharedSnapshot = {
  capabilities: ModuleStudioCapabilities | null;
  unreachable: ModuleStudioUnreachableError | null;
};

const EMPTY_SNAPSHOT: SharedSnapshot = { capabilities: null, unreachable: null };
let snapshot: SharedSnapshot = EMPTY_SNAPSHOT;
let loadStarted = false;
const listeners = new Set<() => void>();

const publish = (next: SharedSnapshot): void => {
  snapshot = next;
  listeners.forEach((notify) => notify());
};

const startLoad = (): void => {
  if (loadStarted) return;
  loadStarted = true;
  void loadModuleStudioCapabilities().then(
    (loaded) => {
      publish({ capabilities: loaded, unreachable: null });
    },
    (error: unknown) => {
      // Aucun cache d'échec : la prochaine lecture réessaie.
      loadStarted = false;
      const unreachable =
        error instanceof ModuleStudioUnreachableError ? error : new ModuleStudioUnreachableError(error);
      publish({ capabilities: null, unreachable });
    }
  );
};

const subscribeCapabilities = (listener: () => void): (() => void) => {
  listeners.add(listener);
  if (snapshot.capabilities === null) {
    startLoad();
  }
  return () => {
    listeners.delete(listener);
  };
};

const readSnapshot = (): SharedSnapshot => snapshot;
const readServerSnapshot = (): SharedSnapshot => EMPTY_SNAPSHOT;

/** « Réessayer » : oublie l'échec et relance la lecture des capacités. */
const retryCapabilities = (): void => {
  if (loadStarted) return;
  publish(EMPTY_SNAPSHOT);
  startLoad();
};

let lastValue: { key: SharedSnapshot; value: ModuleStudioContextValue } | null = null;

const DEMO_TRACKED_RESOURCES = [
  'dossiers', 'specs', 'testPlans', 'testRuns', 'prototypeImports', 'prototypeAnalysis', 'moduleSpec', 'designer', 'preview',
  'generator', 'generatorStripe', 'generatorRepo', 'pipeline', 'pipelineData', 'pipelineGates', 'feedback', 'deliveryCheck', 'presentation', 'customPages', 'translation',
    'simulation', 'translationJobs', 'capabilityCatalog', 'navigationFidelity',
] as ModuleStudioResource[];

const buildValue = (current: SharedSnapshot): ModuleStudioContextValue => {
  if (lastValue && lastValue.key === current) {
    return lastValue.value;
  }
  const { capabilities, unreachable } = current;
  const resolved = capabilities ?? ALL_DEMO_CAPABILITIES;
  const fixture = getSharedFixtureRepository({ currentUserName });
  const rest = new RestRepository();
  const demoResources = DEMO_TRACKED_RESOURCES.filter((resource) => resolved.resources[resource] !== true);
  const value: ModuleStudioContextValue = {
    repository: unreachable ? unreachableRepository(unreachable) : composeRepository(resolved, fixture, rest),
    demoResources,
    capabilities: resolved,
    resetDemo: () => fixture.reset(),
    ready: capabilities !== null,
    unreachable,
    retry: retryCapabilities,
    demoProfile: capabilities === ALL_DEMO_CAPABILITIES,
  };
  lastValue = { key: current, value };
  return value;
};

/** Valeur partagée du module (capacités chargées une fois, dépôt composite mémorisé). */
const useSharedModuleStudioValue = (): ModuleStudioContextValue => {
  const current = useSyncExternalStore(subscribeCapabilities, readSnapshot, readServerSnapshot);
  useEffect(() => {
    // Sécurité : déclenche le chargement même si aucun abonnement n'a encore eu lieu.
    subscribeCapabilities(() => undefined)();
  }, []);
  return buildValue(current);
};

/** Pour les tests : oublie l'état partagé du module. */
export const resetModuleStudioSharedState = (): void => {
  snapshot = EMPTY_SNAPSHOT;
  loadStarted = false;
  lastValue = null;
};

export function ModuleStudioDataProvider({ children }: { children: ReactNode }) {
  const value = useSharedModuleStudioValue();
  return (
    <ModuleStudioContext.Provider value={value}>
      {value.unreachable ? (
        <ServerUnreachable error={value.unreachable} onRetry={value.retry} />
      ) : (
        <>
          {value.demoProfile ? <DemoProfileBanner /> : null}
          {children}
        </>
      )}
    </ModuleStudioContext.Provider>
  );
}

/** Dépôt de données du module + état de bascule (démonstration / API), avec ou sans fournisseur. */
export const useModuleStudio = (): ModuleStudioContextValue => {
  const fromContext = useContext(ModuleStudioContext);
  const shared = useSharedModuleStudioValue();
  return fromContext ?? shared;
};

/** Variante sans garde pour les tests hors fournisseur. */
export const useModuleStudioOptional = (): ModuleStudioContextValue | null =>
  useContext(ModuleStudioContext);
